import { useMutation } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import urls from '../../utils/urls';
import useNotify from '../useNotify';
import { getIntl } from '../../utils/intl';
import { appAPI } from '../../utils/api';

const intl = getIntl('useFastenTefcaSession', {
    errorStartingSession: 'Unable to start a TEFCA session',
});

export type UseFastenTefcaSessionArgs = {
    medicalProfileId: string;
};

export type UseFastenTefcaSessionReturnValue = {
    startSession: () => void;
    session: APITypes.ehrIntegrationsService.FastenTefcaSessionResponse | undefined;
    isStartingSession: boolean;
    hasSessionFailed: boolean;
};

/**
 * Asks the backend for what the TEFCA widget needs: Fasten's sandbox public id and the profile-scoped external
 * id. The backend refuses unless the caller resolves to sandbox mode, so this is only ever called from the
 * sandbox-gated TEFCA screen.
 */
export default function useFastenTefcaSession({
    medicalProfileId,
}: UseFastenTefcaSessionArgs): UseFastenTefcaSessionReturnValue {
    const notify = useNotify();

    const sessionMutation = useMutation({
        mutationFn: async () =>
            appAPI.post<APITypes.ehrIntegrationsService.FastenTefcaSessionResponse>(
                urls.api.ehrIntegrations.fasten.endpoints.tefcaSession(medicalProfileId),
            ),
        onError: () => {
            notify('error', intl('errorStartingSession'));
        },
    });

    return {
        startSession: sessionMutation.mutate,
        session: sessionMutation.data,
        isStartingSession: sessionMutation.isPending,
        hasSessionFailed: sessionMutation.isError,
    };
}
